import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Archive,
  Award,
  BadgeCheck,
  Bell,
  Building2,
  Calendar,
  ChevronLeft,
  ClipboardList,
  Clock,
  CreditCard,
  Eye,
  FileStack,
  FileText,
  FlaskConical,
  LayoutDashboard,
  LifeBuoy,
  Lock,
  LogOut,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Pill,
  QrCode,
  Search,
  Shield,
  ShoppingBag,
  Siren,
  Sparkles,
  Store,
  Target,
  Truck,
  User,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../../auth/AuthContext';
import { NHCLogoSquare } from '@design/index';
import { patientAuthFlow } from '../auth/patientAuthFlow';
import { buildIdentity } from '../utils/patientIdentity';
import { clearCoords, getCoords, requestCoords } from '../utils/patientLocation';
import PatientSystemDesignShell from './PatientSystemDesignShell';
import { patientApi } from '../api';
import { onNotificationsChanged } from '../state/notificationsBus';

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const MONO = "'SF Mono','Fira Code',ui-monospace,monospace";
const INK = "#0B2545", MUT = "#5D7285", BG_SHELL = "#F4F7FA", LINE = "#E7EDF3";
const P_COLOR = "#00B8B8", P_DEEP = "#0A7E88";

interface NavItem { to: string; label: string; icon: LucideIcon }
interface NavGroup { title: string; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { to: '/patient/home', label: 'Home', icon: LayoutDashboard },
      { to: '/patient/nhc-card', label: 'NeoHealthCard Card', icon: CreditCard },
      { to: '/patient/timeline', label: 'Timeline', icon: Clock },
      { to: '/patient/neoai', label: 'NeoAI', icon: Sparkles },
      { to: '/patient/my-chat', label: 'Care Chat', icon: MessageCircle },
    ],
  },
  {
    title: 'Care',
    items: [
      { to: '/patient/care-services', label: 'Care Services', icon: Store },
      { to: '/patient/nearby', label: 'Nearby Care', icon: MapPin },
      { to: '/patient/my-care', label: 'My Care', icon: ClipboardList },
      { to: '/patient/follow-ups', label: 'Appointments', icon: Calendar },
      { to: '/patient/medicines', label: 'Medicines', icon: Pill },
      { to: '/patient/care-plans', label: 'Care Plans', icon: Target },
      { to: '/patient/lab', label: 'Lab Tracking', icon: FlaskConical },
      { to: '/patient/hospital', label: 'Hospital Record', icon: Building2 },
      { to: '/patient/pharmacy', label: 'Pharmacy', icon: ShoppingBag },
      { to: '/patient/emergency', label: 'Emergency', icon: Siren },
      { to: '/patient/ambulance', label: 'Ambulance', icon: Truck },
    ],
  },
  {
    title: 'Records',
    items: [
      { to: '/patient/health-vault', label: 'Health Vault', icon: Archive },
      { to: '/patient/lab-reports', label: 'Lab Reports', icon: FileText },
      { to: '/patient/certificates', label: 'Certificates', icon: Award },
      { to: '/patient/neoverify', label: 'NeoVerify', icon: BadgeCheck },
    ],
  },
  {
    title: 'Trust & Insurance',
    items: [
      { to: '/patient/consent', label: 'Consent Center', icon: Shield },
      { to: '/patient/access-logs', label: 'Access History', icon: Eye },
      { to: '/patient/insurance', label: 'Insurance', icon: FileStack },
    ],
  },
  {
    title: 'Account',
    items: [
      { to: '/patient/profile', label: 'Profile', icon: User },
      { to: '/patient/profile/addresses', label: 'Addresses', icon: MapPin },
      { to: '/patient/my-requests', label: 'My Requests', icon: LifeBuoy },
      { to: '/patient/security', label: 'Security', icon: Lock },
    ],
  },
];

function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const DESKTOP_CONTENT_STYLE = `
  .patient-desktop-content > div {
    max-width: none !important;
    width: 100% !important;
  }
  .patient-desktop-content > div > div[style*="maxWidth"] {
    max-width: 1280px !important;
    width: 100% !important;
  }
  .patient-desktop-content > div[style*="max-width: 900px"],
  .patient-desktop-content > div[style*="maxWidth"] {
    max-width: 1280px !important;
    width: 100% !important;
  }
  .patient-desktop-content [class*="space-y-"] > div[style*="maxWidth"] {
    max-width: 1280px !important;
  }
`;

function NHCMark({ width = 30, mode = 'color' }: { width?: number; mode?: 'color' | 'white' }) {
  const h = Math.round(width * (140 / 220));
  const l = mode === 'white' ? '#fff' : P_COLOR, r = mode === 'white' ? 'rgba(255,255,255,0.8)' : '#002B5C', ecg = mode === 'white' ? 'rgba(0,40,80,0.5)' : '#fff';
  return (
    <svg width={width} height={h} viewBox="0 0 220 140" fill="none">
      <circle cx="73" cy="70" r="66" fill={l} /><circle cx="147" cy="70" r="66" fill={r} />
      <clipPath id="mkN"><circle cx="147" cy="70" r="66" /></clipPath>
      <circle cx="73" cy="70" r="66" fill={l} clipPath="url(#mkN)" opacity="0.9" />
      <path d="M52,70 L86,70 L96,84 L110,36 L124,84 L134,70 L168,70" stroke={ecg} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PatientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationActive, setLocationActive] = useState(() => !!getCoords());
  const [locationDenied, setLocationDenied] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, []);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeSearch(); };
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) closeSearch();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [searchOpen, closeSearch]);

  const [mobileNhcId] = useState(() => buildIdentity(null).nhcIdDisplay);

  // Single source of truth for the notification indicator — real unread count
  // from the backend. Dot renders ONLY when unread > 0 (no static double dots).
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      patientApi.getNotifications()
        .then((list) => { if (alive) setUnreadCount((list ?? []).filter(n => !n.read).length); })
        .catch(() => { if (alive) setUnreadCount(0); });
    };
    refresh();
    // The Notifications page marks rows read on the server; follow it at once
    // instead of leaving a stale count until the next navigation.
    const off = onNotificationsChanged(refresh);
    return () => { alive = false; off(); };
  }, [location.pathname]);

  const authState = patientAuthFlow.getState();
  const nhcDisplayId = (authState.nhcId && authState.approvalStatus === 'approved') ? authState.nhcId : 'NeoHealthCard ID pending';

  if (isMobile) {
    return (
      <PatientSystemDesignShell role="patient" userName={user?.displayName?.split(' ')[0] || 'Patient'} nhcId={mobileNhcId} showNotificationDot={unreadCount > 0}>
        <div className="p-4">
          <Outlet />
        </div>
      </PatientSystemDesignShell>
    );
  }

  const displayName = user?.displayName || 'Patient';
  const initials = displayName.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();

  const SEARCH_SUGGESTIONS: { section: string; items: { title: string; subtitle: string; route: string; badge?: string; badgeColor?: string }[] }[] = [
    { section: 'Nearby Care', items: [
      { title: 'Doctors near you', subtitle: 'Verified specialists and GPs', route: '/patient/care-services/doctors', badge: 'Nearby', badgeColor: '#0891B2' },
      { title: 'Labs with home collection', subtitle: 'NABL certified, sample pickup', route: '/patient/care-services/labs', badge: 'Home collection', badgeColor: '#00B8B8' },
      { title: 'Open pharmacies', subtitle: 'Medicine delivery and eRx', route: '/patient/care-services/pharmacy', badge: '24x7', badgeColor: '#059669' },
      { title: 'Hospitals with emergency', subtitle: 'ER, OPD, and admissions', route: '/patient/care-services/hospitals', badge: 'Nearby', badgeColor: '#DC2626' },
    ]},
    { section: 'Care Services', items: [
      { title: 'Book Doctor', subtitle: 'Find and consult verified doctors', route: '/patient/care-services/doctors', badge: 'NeoVerified', badgeColor: '#0A7B4F' },
      { title: 'Start Telemedicine', subtitle: 'Video consult from home', route: '/patient/care-services/telemedicine', badge: '24x7', badgeColor: '#0891B2' },
      { title: 'Book Lab Test', subtitle: 'Blood tests, health packages', route: '/patient/labs', badge: 'Home collection', badgeColor: '#00B8B8' },
      { title: 'Book Imaging / Scan', subtitle: 'MRI, CT, X-Ray, Ultrasound', route: '/patient/care-services/imaging', badge: 'Same day', badgeColor: '#4F46E5' },
      { title: 'Order Medicines', subtitle: 'Pharmacy delivery and refills', route: '/patient/pharmacy', badge: 'Delivery', badgeColor: '#059669' },
      { title: 'Request Ambulance', subtitle: 'ALS/BLS emergency dispatch', route: '/patient/emergency', badge: 'SOS', badgeColor: '#DC2626' },
      { title: 'Check Insurance Coverage', subtitle: 'Claims, pre-auth, plans', route: '/patient/insurance', badge: 'Insurance', badgeColor: '#7C3AED' },
    ]},
    { section: 'Records & Documents', items: [
      { title: 'Health Vault', subtitle: 'All your health records', route: '/patient/health-vault' },
      { title: 'Medical Documents', subtitle: 'Reports, letters, summaries', route: '/patient/documents' },
      { title: 'Lab Reports', subtitle: 'Test results and history', route: '/patient/lab-reports' },
      { title: 'Prescriptions', subtitle: 'Current and past prescriptions', route: '/patient/prescriptions' },
    ]},
    { section: 'Suggested Searches', items: [
      { title: 'fever doctor near me', subtitle: 'Find doctors for fever', route: '/patient/care-services/doctors' },
      { title: 'CBC test home collection', subtitle: 'Book CBC with home pickup', route: '/patient/labs' },
      { title: 'upload prescription', subtitle: 'Upload to Health Vault', route: '/patient/health-vault' },
      { title: 'compare medicine price', subtitle: 'Pharmacy price comparison', route: '/patient/care-services/pharmacy' },
      { title: 'insurance claim documents', subtitle: 'Prepare claim paperwork', route: '/patient/claims' },
      { title: 'emergency ambulance', subtitle: 'Request emergency dispatch', route: '/patient/emergency' },
    ]},
  ];

  const q = searchQuery.toLowerCase().trim();
  const filteredSuggestions = q.length > 0
    ? SEARCH_SUGGESTIONS.map(s => ({
        ...s,
        items: s.items.filter(i =>
          i.title.toLowerCase().includes(q) ||
          i.subtitle.toLowerCase().includes(q) ||
          (i.badge && i.badge.toLowerCase().includes(q))
        ),
      })).filter(s => s.items.length > 0)
    : SEARCH_SUGGESTIONS;

  const hasResults = filteredSuggestions.some(s => s.items.length > 0);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: FONT, background: BG_SHELL }}>
      <style dangerouslySetInnerHTML={{ __html: DESKTOP_CONTENT_STYLE }} />

      {/* ═══ Sidebar — matches NeoHealthCardNetworkDashboard.jsx Sidebar exactly ═══ */}
      <aside style={{
        width: collapsed ? 76 : 248,
        flexShrink: 0, background: '#fff', borderRight: `1px solid ${LINE}`,
        height: '100%', display: 'flex', flexDirection: 'column',
        overflow: 'hidden', transition: 'width .22s ease',
      }}>
        {/* Sidebar header — gradient logo square + system name */}
        <div style={{ padding: collapsed ? '18px 0' : '20px 18px 14px', display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: `linear-gradient(135deg,${P_COLOR},${P_DEEP})`, display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0, boxShadow: `0 6px 14px ${P_COLOR}55` }}>MH</div>
          {!collapsed && <div style={{ minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 800, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>My Health</div><div style={{ fontSize: 10.5, color: MUT, fontWeight: 600, marginTop: 1 }}>Patient workspace</div></div>}
        </div>

        {/* Nav groups */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: collapsed ? '6px 10px' : '6px 12px 12px' }}>
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.title} style={{ marginTop: gi === 0 ? 4 : 14 }}>
              {!collapsed && (
                <div style={{
                  padding: '0 10px 7px',
                  fontSize: 10, fontWeight: 800, color: '#9AA9B7',
                  letterSpacing: '0.1em', textTransform: 'uppercase' as const,
                }}>{group.title}</div>
              )}
              {group.items.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to || (item.to === '/patient/home' && location.pathname === '/patient');
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 11,
                      padding: collapsed ? '11px 0' : '9px 10px',
                      borderRadius: 10, fontSize: 13.5,
                      fontWeight: isActive ? 800 : 600,
                      color: isActive ? P_DEEP : '#46566A',
                      background: isActive ? `${P_COLOR}14` : 'transparent',
                      transition: 'all 0.12s ease',
                      textDecoration: 'none',
                      justifyContent: collapsed ? 'center' : undefined,
                      marginBottom: 2,
                      position: 'relative' as const,
                    }}
                    title={collapsed ? item.label : undefined}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.color = '#1F2937'; } }}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#46566A'; } }}
                  >
                    {isActive && !collapsed && <span style={{ position: 'absolute', left: -12, top: 8, bottom: 8, width: 3, borderRadius: 3, background: P_COLOR }} />}
                    <Icon style={{ width: 19, height: 19, flexShrink: 0, color: isActive ? P_DEEP : '#7F8EA0' }} />
                    {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Sidebar footer — Avatar with conic-gradient ring + Mark logo */}
        <div style={{ borderTop: `1px solid ${LINE}`, padding: collapsed ? '12px 0' : '12px 14px', display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start' }}>
          {collapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', padding: 2, background: `conic-gradient(from 140deg, ${P_COLOR}, ${P_DEEP}, ${P_COLOR})`, flexShrink: 0, boxShadow: '0 2px 8px rgba(10,37,64,0.18)' }}>
                <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: `linear-gradient(135deg, ${P_COLOR}, ${P_DEEP})`, display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 800, fontSize: 12, boxShadow: 'inset 0 0 0 2px #fff' }}>{initials}</div>
              </div>
              <button onClick={logout} title="Log out" style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                <LogOut style={{ width: 18, height: 18, color: '#C0606A' }} />
              </button>
            </div>
          ) : (
            <>
              <div style={{ width: 40, height: 40, borderRadius: '50%', padding: 2, background: `conic-gradient(from 140deg, ${P_COLOR}, ${P_DEEP}, ${P_COLOR})`, flexShrink: 0, boxShadow: '0 2px 8px rgba(10,37,64,0.18)' }}>
                <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: `linear-gradient(135deg, ${P_COLOR}, ${P_DEEP})`, display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 800, fontSize: 14, boxShadow: 'inset 0 0 0 2px #fff' }}>{initials}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</span>
                  <NHCMark width={13} />
                </div>
                <div style={{ fontSize: 10.5, color: MUT, marginTop: 1 }}>Patient</div>
              </div>
              <button onClick={logout} title="Log out" style={{ background: BG_SHELL, border: `1px solid ${LINE}`, borderRadius: 9, width: 32, height: 32, display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <LogOut style={{ width: 17, height: 17, color: '#C0606A' }} />
              </button>
            </>
          )}
        </div>
      </aside>

      {/* ═══ Main area ═══ */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* ═══ Header — matches NeoHealthCardNetworkDashboard.jsx Header exactly ═══ */}
        {/* position+zIndex lift the header's stacking context (backdropFilter creates
            one) ABOVE the scrolling content, so the search dropdown never renders
            behind the identity card below. */}
        <header style={{ borderBottom: `1px solid ${LINE}`, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', flexShrink: 0, position: 'relative', zIndex: 40 }}>
          <div style={{ padding: '11px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Menu toggle */}
            <button onClick={() => setCollapsed(c => !c)} style={{ background: BG_SHELL, border: `1px solid ${LINE}`, borderRadius: 10, width: 38, height: 38, display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
              <Menu style={{ width: 20, height: 20, color: '#46566A' }} />
            </button>

            {/* System name + secure session */}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: INK, whiteSpace: 'nowrap' }}>My Health</div>
              <div style={{ fontSize: 11, color: MUT, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} /> Secure session
              </div>
            </div>

            {/* Location button */}
            <button
              onClick={() => {
                // The fix was being read and thrown away, so "Nearby active"
                // lit up while nothing on any page could say how far anything
                // was. It is kept now, and only in the browser.
                if (locationActive) { clearCoords(); setLocationActive(false); setLocationDenied(false); return; }
                void requestCoords().then((c) => {
                  setLocationActive(!!c);
                  setLocationDenied(!c);
                });
              }}
              title={locationActive ? 'Location active' : 'Enable location'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: BG_SHELL, border: `1px solid ${LINE}`, borderRadius: 12, padding: '10px 12px', cursor: 'pointer', flexShrink: 0 }}
            >
              <MapPin style={{ width: 16, height: 16, color: locationActive ? P_DEEP : (locationDenied ? '#EF4444' : P_DEEP) }} />
              <span style={{ fontSize: 12.5, fontWeight: 700, color: INK, whiteSpace: 'nowrap' }}>{locationActive ? 'Nearby active' : 'Set location'}</span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={MUT} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
            </button>

            {/* Search bar */}
            <div ref={searchRef} style={{ position: 'relative', flex: 1, maxWidth: 580, marginLeft: 0 }}>
              <div
                onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 0); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  background: searchOpen ? '#FFFFFF' : BG_SHELL,
                  border: searchOpen ? `1px solid ${P_COLOR}` : `1px solid ${LINE}`,
                  borderRadius: searchOpen && searchQuery.trim() ? '12px 12px 0 0' : 12,
                  padding: '10px 13px', cursor: 'text',
                  boxShadow: searchOpen ? `0 2px 12px ${P_COLOR}18` : 'none',
                  transition: 'all 0.15s ease',
                }}
              >
                <Search style={{ width: 17, height: 17, color: searchOpen ? P_COLOR : '#9AA9B7', flexShrink: 0 }} />
                {searchOpen ? (
                  <input
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && searchQuery.trim()) {
                        closeSearch();
                        navigate('/patient/care-services');
                      }
                    }}
                    placeholder="Search care, records, medicines..."
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: INK, background: 'transparent', fontFamily: 'inherit' }}
                  />
                ) : (
                  <span style={{ fontSize: 13, color: '#9AA9B7', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>Search care, records, medicines...</span>
                )}
                {searchOpen && searchQuery && (
                  <button onClick={(e) => { e.stopPropagation(); setSearchQuery(''); searchInputRef.current?.focus(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'grid', placeItems: 'center' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                )}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
                  <kbd style={{ fontSize: 10, color: MUT, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 5, padding: '2px 6px', fontFamily: MONO }}>⌘K</kbd>
                </span>
              </div>

              {/* Suggestion Panel — opens only after the user TYPES, so it never
                  covers the dashboard card on focus. Height-limited + scrollable. */}
              {searchOpen && searchQuery.trim() !== '' && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                  background: '#FFFFFF', borderRadius: '0 0 12px 12px',
                  border: `1px solid ${LINE}`, borderTop: 'none',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.05)',
                  maxHeight: 320, overflowY: 'auto',
                }}>
                  {locationActive && (
                    <div style={{ padding: '6px 16px', background: '#F0FDFA', borderBottom: '1px solid #99F6E4', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MapPin style={{ width: 12, height: 12, color: P_COLOR }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: P_DEEP }}>Location on — shortcuts below open location-aware pages</span>
                    </div>
                  )}
                  {hasResults ? (
                    filteredSuggestions.map(section => (
                      <div key={section.section}>
                        <div style={{ padding: '10px 16px 4px', fontSize: 9, fontWeight: 700, color: section.section === 'Nearby Care' && locationActive ? P_DEEP : '#9AA9B7', letterSpacing: '0.06em', textTransform: 'uppercase' as const, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {section.section === 'Nearby Care' && locationActive && <MapPin style={{ width: 9, height: 9 }} />}
                          {section.section}
                        </div>
                        {section.items.map(item => (
                          <div
                            key={item.title + item.route}
                            onClick={() => { closeSearch(); navigate(item.route); }}
                            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#F8FFFE'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
                              cursor: 'pointer', transition: 'background 0.1s',
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: INK, lineHeight: 1.3 }}>{item.title}</div>
                              <div style={{ fontSize: 10, color: '#9AA9B7', marginTop: 1 }}>{item.subtitle}</div>
                            </div>
                            {item.badge && (
                              <span style={{
                                fontSize: 8, fontWeight: 700, color: item.badgeColor || P_COLOR,
                                background: (item.badgeColor || P_COLOR) + '10', padding: '2px 7px',
                                borderRadius: 4, flexShrink: 0, whiteSpace: 'nowrap' as const,
                              }}>{item.badge}</span>
                            )}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}><path d="M9 18l6-6-6-6" /></svg>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    <div
                      onClick={() => { closeSearch(); navigate('/patient/care-services'); }}
                      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#F8FFFE'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer' }}
                    >
                      <Search style={{ width: 14, height: 14, color: P_COLOR, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: INK }}>Search Care Services for: <span style={{ color: P_COLOR }}>{searchQuery}</span></div>
                        <div style={{ fontSize: 10, color: '#9AA9B7', marginTop: 1 }}>Browse all care services and providers</div>
                      </div>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}><path d="M9 18l6-6-6-6" /></svg>
                    </div>
                  )}
                  <div style={{ padding: '8px 16px 10px', borderTop: `1px solid ${LINE}`, background: '#FAFBFC' }}>
                    <div style={{ fontSize: 8.5, color: '#9AA9B7', lineHeight: 1.5, textAlign: 'center' as const }}>
                      Care search helps you find services. NeoHealthCard does not replace emergency medical care. For emergencies, use SOS or request ambulance.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 26, background: LINE, flexShrink: 0 }} />

            {/* Bell */}
            <button
              onClick={() => navigate('/patient/notifications')}
              style={{ background: BG_SHELL, border: `1px solid ${LINE}`, borderRadius: 10, width: 40, height: 40, display: 'grid', placeItems: 'center', cursor: 'pointer', position: 'relative', flexShrink: 0 }}
              title="Notifications"
            >
              <Bell style={{ width: 19, height: 19, color: '#46566A' }} />
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: 8, right: 9, width: 8, height: 8, borderRadius: '50%', background: '#EF4444', border: '2px solid #fff' }} />
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="patient-desktop-content" style={{ flex: 1, overflowY: 'auto', padding: 22 }}>
          <div style={{ maxWidth: 1280, width: '100%', margin: '0 auto' }}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Floating SOS Button */}
      <button
        onClick={() => navigate('/patient/emergency')}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all active:scale-95 group"
        aria-label="Emergency SOS"
      >
        <div className="absolute inset-0 rounded-full bg-red-600 animate-ping opacity-20" />
        <div className="flex flex-col items-center relative">
          <Phone className="w-6 h-6" />
          <span className="text-[9px] font-bold mt-0.5">SOS</span>
        </div>
      </button>
    </div>
  );
}
